import styled, {css} from "styled-components";
import React, {HTMLAttributes, JSX, LazyExoticComponent, ReactElement} from "react";
import {motion} from "motion/react";
import IconType from "../../assets/icons";
import {ifProp, switchProp} from "styled-tools";
import {getColorFromTheme} from "../../utils/style.utils";

export type DrawerSide = "left" | "right" | "top" | "bottom";
export type DrawerSize = "sm" | "md" | "lg" | "xl" | "full";

interface DrawerStyleProps extends HTMLAttributes<HTMLDivElement> {
    $backdrop: boolean;
    $side?: DrawerSide;
    $size?: DrawerSize;
    $backgroundColor?: string;
    $fitScreenOnSmall?: boolean;
}

export interface DrawerProps extends DrawerStyleProps {
    open: boolean;
    onHide?: () => void;
    onShow?: () => void;
    onToggle?: () => void;
    onClose?: () => void;
    onOpenChange: (open: boolean) => void;
    overlay?: boolean;
    closeOnEscape?: boolean;
    closeOnOutside?: boolean;
    lockScroll?: boolean;
    showCloseButton?: boolean;
    closeIcon?: string | JSX.Element | LazyExoticComponent<React.FC<IconType>> | ReactElement;
    ariaLabel?: string;
    className?: string;
    headerClassName?: string;
    overlayClassName?: string;
    children?: React.ReactNode;
    header?: React.ReactNode;
    footer?: React.ReactNode;
    backdropClasses?: string;
    backBtnDisplay?: boolean;
}

export const Wrapper = styled.div`
    position: fixed;
    inset: 0;
    z-index: 10000;

    .backdrop-overlay {
        position: absolute;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.42);
        backdrop-filter: blur(1px);
    }

    ${({theme}: { theme: any }) => theme.breakpoints.down("md")} {
        div, span, input, h4, h1, h2, h3, h4, h5, h6, p, blockquote, small {
            font-family: var(--font-sans) !important;
        }
    }
`

export const DrawerPanelStyle = styled(motion.div)<DrawerStyleProps>`
    position: absolute;
    background: ${({$backgroundColor}) => $backgroundColor || "var(--color-secondary)"};
    box-shadow: var(--shadow-md);
    outline: 2px solid transparent;
    outline-offset: 2px;
    display: flex;
    flex-direction: column;
    gap: 0;
    overflow: hidden;

    ${({theme}: { theme: any }) => theme.breakpoints.up("md")} {
        border-radius: var(--radius-xl);
        margin: var(--space-2);
    }


    //min-height: 97vh;

    ${switchProp('$size', {
        sm: css`
            width: calc(var(--space-4) * 20);
            height: calc(var(--space-12) * 4);
        `,
        md: css`
            width: calc(var(--space-8) * 25);
            height: calc(var(--space-8) * 8);
        `,
        lg: css`
            width: calc(var(--space-4) * 30);
            height: calc(var(--space-8) * 10);
        `,
        xl: css`
            width: 32rem;
            height: 28rem;
        `,
        full: css`
            width: 100vw;
            height: 100vh;
        `,
    })}
    ${switchProp('$side', {
        left: css`
            left: 0;
            top: 0;
            height: 100%;
            max-height: 97vh;
        `,
        right: css`
            right: 0;
            top: 0;
            height: 100%;
            max-height: 97vh;

        `,
        top: css`
            top: 30px;
            width: 99%;
            border-radius: 1rem;
            left: 50% !important;
            transform: translateX(-50%);
        `,
        bottom: css`
            bottom: 30px;
            width: 95%;
            height: auto;
            max-height: calc(100vh - 90px);
            border-radius: 1rem;
            left: 50% !important;
            transform: translateX(-50%) !important;

            ${({theme}: { theme: any }) => theme.breakpoints.down("md")} {
                bottom: 0px;
                max-height: calc(100vh - 10px);
            }
        `
    })}
    ${({theme}: { theme: any }) => theme.breakpoints.down("md")} {
        ${switchProp('$side', {
            left: css`
                max-height: 100vh;
            `,
            right: css`
                max-height: 100vh;
            `
        })}

        ${ifProp(
                '$fitScreenOnSmall',
                css`
                    width: 100% !important;
                    height: 100vh !important;
                    max-height: 100%;
                    top: 0;
                    left: 0;
                `,
        )}
    }
`
export const DrawerHeaderStyle = styled.div<Pick<DrawerStyleProps, "$side">>`
    display: flex;
    align-items: self-end;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid color-mix(in srgb, var(--color-border) 30%, transparent);

    .back-btn {
        box-shadow: none !important;
    }

    > .header {
        min-height: 1.75rem;
        user-select: none;
        font-family: var(--font-grotesk);
        font-weight: 500;
        font-size: var(--step-1);
        line-height: 100%;
        letter-spacing: 0;
        text-align: center;
        color: var(--color-text-dark);
    }

    button.close-btn {
        border-radius: var(--radius-lg);
        background-color: var(--color-secondary-dark) !important;

        &:hover {
            background: var(--color-primary-hover) !important;
        }
    }

    .back-btn {
        display: none;
    }

    ${({theme}: { theme: any }) => theme.breakpoints.down("md")} {
        border-bottom-color: transparent;
        padding: 1rem 1rem 0rem;

        > .header {
            font-weight: 600;
            color: var(--color-surface);
            letter-spacing: -2%;
            ${switchProp('$side', {
                left: css`
                    font-weight: 700;
                `,
                right: css`
                    font-weight: 700;
                `,
                top: css`
                    font-weight: 500;
                    font-size: 16px;
                `,
                bottom: css`
                    font-weight: 500;
                    font-size: 16px;
                `
            })}

        }

        .back-btn {
            display: block;
        }
    }
`
export const DrawerContentStyle = styled.div`
    padding: var(--space-4);
    overflow-y: auto;
    height: 100%;
    margin-bottom: var(--space-3);
    margin-top: var(--space-2);

    > div {
        position: relative;
        height: 100%;
    }

    span.mode-txt {
        font-size: calc(var(--step--1) * 0.9);
        font-weight: 500;
        line-height: 100%;
        letter-spacing: 0%;
        color: var(--color-text-light);
    }

    h4 {
        font-size: var(--step-0);
        font-weight: 500;
        color: var(--color-text-dark);
    }

    ${({theme}: { theme: any }) => theme.breakpoints.down("md")} {
        padding: var(--space-4) var(--space-4) 5px;

        span.mode-txt {
            padding-bottom: 8px;
            text-align: center;
            margin: 0 auto;
            max-width: 60%;
            line-height: 18px;
        }
    }
`
export const DrawerCustomStyle = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 20px;
    height: inherit;

    //.input-title {
    //    margin-bottom: 10px;
    //}

    label {
        color: var(--color-text-light);
    }

    .actions {
        margin-top: auto;
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
    }

    h4 {
        margin-bottom: 0.5rem;
        font-family: var(--font-grotesk);
        font-weight: 500;
        font-size: var(--step--1);
        line-height: 100%;
        letter-spacing: 0%;
        color: var(--color-text-light);
    }
`
export const DrawerFooterStyle = styled.div``